import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { AUTH_SIGNUP_URL } from "@/lib/data";
import { MFT, SP } from "@/components/site/BrandLinks";

const ease = [0.22, 1, 0.36, 1];

const ILLUSTRATION =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_memory-preserve-8/artifacts/5ia8soxr_familytree-illustration.jpg";

/* ------------------------------------------------------------------ */
/*  Reusable typographic primitives                                    */
/* ------------------------------------------------------------------ */

function Chapter({ n, title, subtitle, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease }}
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 md:py-20 border-t border-[color:var(--ink-line)]"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14 items-end">
        <div className="col-span-12 md:col-span-2">
          <div className="numeric-index text-[color:var(--ink-soft)]">{n}</div>
        </div>
        <div className="col-span-12 md:col-span-10">
          <h2
            className="font-serif-display leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(30px, 4vw, 58px)" }}
          >
            {title}
            {subtitle ? (
              <>
                {" "}
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  {subtitle}
                </span>
              </>
            ) : null}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="hidden md:block md:col-span-2" />
        <div className="col-span-12 md:col-span-10">{children}</div>
      </div>
    </motion.section>
  );
}

function Body({ children }) {
  return (
    <p
      className="text-[17px] md:text-[19px] leading-[1.65] max-w-[62ch] text-[color:var(--ink)]"
      style={{ fontFeatureSettings: '"ss01"' }}
    >
      {children}
    </p>
  );
}

function Prose({ children }) {
  return <div className="space-y-6 md:space-y-7">{children}</div>;
}

function Ledger({ items, columns = 2 }) {
  const colClass = columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <ul
      className={`mt-2 grid grid-cols-1 ${colClass} gap-x-10 gap-y-0 border-t border-[color:var(--ink-line)]`}
    >
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-baseline gap-4 py-3 border-b border-[color:var(--ink-line)]"
        >
          <span className="numeric-index text-[color:var(--ink-soft)] w-8 shrink-0">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span
            className="font-serif-display leading-[1.15]"
            style={{ fontSize: "clamp(17px, 1.5vw, 22px)" }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ */
/*  Content data                                                       */
/* ------------------------------------------------------------------ */

const PRESERVE_ITEMS = [
  "Family biographies",
  "Photographs and albums",
  "Videos and voice recordings",
  "Important life events",
  "Birthdays and anniversaries",
  "Family recipes",
  "Letters and journals",
  "Certificates and documents",
  "Awards and achievements",
  "Military service",
  "Education and careers",
  "Community service",
  "Family traditions",
  "Cultural heritage",
  "Migration journeys",
  "Personal memories shared by relatives",
];

const PLACES = [
  "Countries",
  "States and provinces",
  "Cities and towns",
  "Villages",
  "Ancestral homes",
  "Schools",
  "Places of worship",
  "Cemeteries and memorials",
  "Businesses",
  "Historic landmarks",
  "Migration routes",
];

const MILESTONES = [
  "Births",
  "Graduations",
  "Weddings",
  "Anniversaries",
  "Family reunions",
  "Cultural traditions",
  "Religious celebrations",
  "Professional achievements",
  "Community contributions",
  "Military service",
  "Retirement",
  "Memorials",
  "Family milestones",
];

const MEMORIAL_ITEMS = [
  "Life biographies",
  "Photo galleries",
  "Videos",
  "Audio recordings",
  "Family memories",
  "Tributes",
  "Guest messages",
  "Historical documents",
  "Memorial anniversaries",
  "Charitable legacies",
];

const HERITAGE_ITEMS = [
  "Languages",
  "Festivals",
  "Food and recipes",
  "Traditional clothing",
  "Music and dance",
  "Family occupations",
  "Community customs",
  "Religious traditions",
  "Village histories",
  "Regional heritage",
  "Historical migrations",
];

const ARCHIVE_ITEMS = [
  "Old family photographs",
  "Historical documents",
  "Stories from elders",
  "Audio interviews",
  "Videos",
  "Family recipes",
  "Newspaper clippings",
  "Certificates",
  "School records",
  "Military records",
  "Migration stories",
];

const PULSE_ITEMS = [
  "Family members",
  "Historical events",
  "Places and communities",
  "Nature and landscapes",
  "Local weather history",
  "Cultural traditions",
  "Heritage landmarks",
  "Historic photographs",
  "Newspapers and archives",
  "Community discussions",
];

const FUTURE_ITEMS = [
  "The voice of their great-grandmother.",
  "The photographs of an ancestral village.",
  "The handwritten recipe that became a family tradition.",
  "The military medals earned by a great-grandfather.",
  "The migration journey that brought the family to a new country.",
  "The stories that might otherwise have been forgotten.",
];

const CLOSING_LINES = [
  "Every branch has a beginning.",
  "Every generation adds new memories.",
  "Every photograph tells a story.",
  "Every ancestor leaves a legacy.",
  "Every family deserves to be remembered.",
];

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function About() {
  return (
    <main data-testid="about-page" className="pt-32 md:pt-40">
      {/* ---------------- Masthead ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="flex items-baseline justify-between mb-8 md:mb-12 text-[color:var(--ink-soft)]">
          <div className="overline">§ About · Vol. I</div>
          <div className="numeric-index hidden md:block">
            A living archive · An open letter to family
          </div>
        </div>

        <div className="overline text-[color:var(--rust)] mb-4">
          Welcome to MyFamilyTree
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          className="font-serif-display leading-[0.88] tracking-[-0.03em] max-w-6xl"
          style={{ fontSize: "clamp(52px, 10vw, 180px)" }}
        >
          Your story{" "}
          <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
            begins here.
          </span>
        </motion.h1>
      </section>

      {/* ---------------- Opening pull-quote ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="border-y border-[color:var(--ink)] py-10 md:py-16">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="col-span-1 md:col-span-1">
              <span
                className="font-serif-display text-[color:var(--rust)] leading-none"
                style={{ fontSize: "clamp(60px, 8vw, 120px)" }}
              >
                “
              </span>
            </div>
            <div className="col-span-11 md:col-span-11">
              <p
                className="font-italic-serif italic leading-[1.15]"
                style={{ fontSize: "clamp(26px, 4.2vw, 62px)" }}
              >
                Every family has a beginning. Every generation adds a new
                chapter. Every memory deserves to be preserved.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- Opening prose (two paragraphs before the illustration) ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="hidden md:block md:col-span-2">
            <div className="overline text-[color:var(--ink-soft)]">§ Prologue</div>
          </div>
          <div className="col-span-12 md:col-span-10">
            <Prose>
              <p
                className="font-serif-display leading-[1.15]"
                style={{ fontSize: "clamp(22px, 2.4vw, 34px)" }}
              >
                Behind every person is a family. Behind every family is a story.
              </p>

              <Body>
                Some stories span centuries, passing through villages, towns,
                cities, and countries. Others begin with a single courageous
                journey — a new home, a new opportunity, or a new generation.
                Together, these stories form the rich tapestry of our lives.
              </Body>

              <Body>
                <MFT>MyFamilyTree</MFT> is your personal space within the <SP />{" "}
                platform to discover your roots, preserve your family history,
                celebrate your heritage, and create a lasting legacy for
                generations to come. Whether your family has lived in the same
                community for hundreds of years or has spread across the
                world, every name, every photograph, every milestone, and every
                memory is part of a story worth preserving.
              </Body>
            </Prose>
          </div>
        </div>
      </section>

      {/* ---------------- Illustration ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <motion.figure
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease }}
          className="relative max-w-5xl mx-auto"
        >
          <div className="border border-[color:var(--ink-line-strong)] p-3 md:p-5 bg-[color:var(--bone-2)]/50">
            <div className="overflow-hidden">
              <img
                src={ILLUSTRATION}
                alt="Illustration of a multi-generational family tree with portraits of ancestors, ancestral village, city skyline, and icons of memories, records, journeys and traditions"
                className="w-full h-auto block"
                loading="lazy"
                data-testid="about-illustration"
              />
            </div>
          </div>
          <figcaption className="mt-4 flex items-start justify-between gap-6">
            <div className="numeric-index text-[color:var(--ink-soft)]">
              Plate № 001 · The Living Tree
            </div>
            <div className="numeric-index text-[color:var(--ink-soft)] text-right max-w-[260px] hidden md:block">
              Our Roots · Our Strength · Our Family · Our Future
            </div>
          </figcaption>
        </motion.figure>
      </section>

      {/* ---------------- Chapter 01 · More Than a Family Tree ---------------- */}
      <Chapter n="01" title="More than a family tree." subtitle="A living story.">
        <Prose>
          <Body>
            A family tree is not simply a chart of names and dates. It is a
            living story of love, resilience, traditions, achievements, and
            shared experiences. It connects grandparents to grandchildren,
            villages to cities, and past generations to future ones.
          </Body>
          <Body>
            With MyFamilyTree, you can build a rich digital history that grows
            over time. Create detailed family relationships, preserve treasured
            memories, document important milestones, and connect every person
            to the places and events that shaped their lives.
          </Body>
          <p
            className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3]"
            style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
          >
            Your family tree becomes more than genealogy — it becomes your
            family's living legacy.
          </p>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 02 · Every Life Leaves a Legacy ---------------- */}
      <Chapter n="02" title="Every life" subtitle="leaves a legacy.">
        <Prose>
          <Body>
            Every person contributes something unique to their family.
          </Body>
          <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-2 py-2">
            {[
              "Some built homes.",
              "Some cultivated farms.",
              "Some served in the military.",
              "Some taught children.",
              "Some started businesses.",
              "Some cared for their communities.",
              "Some quietly raised generations with love, wisdom, and sacrifice.",
            ].map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
              >
                {line}
              </p>
            ))}
          </div>
          <Body>
            Whether famous or unknown, every individual leaves behind a legacy
            that deserves to be remembered. MyFamilyTree helps preserve these
            stories before they are lost.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 03 · Preserve More Than Names ---------------- */}
      <Chapter n="03" title="Preserve more" subtitle="than names.">
        <Prose>
          <Body>
            Record the moments that matter most. Create beautiful profiles that
            include:
          </Body>
          <Ledger items={PRESERVE_ITEMS} columns={2} />
          <Body>
            Over time, these become a priceless archive for future generations.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 04 · Connect to the World ---------------- */}
      <Chapter n="04" title="Connect your family" subtitle="to the world.">
        <Prose>
          <Body>
            Every family has roots in places that helped shape its story. With
            MyFamilyTree, connect family members to:
          </Body>
          <Ledger items={PLACES} columns={3} />
          <Body>
            Explore how your family's journey intersects with local history,
            regional culture, and significant historical events.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 05 · Celebrate Every Generation ---------------- */}
      <Chapter n="05" title="Celebrate" subtitle="every generation.">
        <Prose>
          <Body>Life is filled with moments worth remembering. Celebrate:</Body>
          <Ledger items={MILESTONES} columns={3} />
          <p
            className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3]"
            style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
          >
            Every generation adds another chapter to your family's story.
          </p>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 06 · Honor Those Who Came Before ---------------- */}
      <Chapter n="06" title="Honor those" subtitle="who came before you.">
        <Prose>
          <Body>
            The people we love continue to live through the memories they leave
            behind.
          </Body>
          <Body>
            MyFamilyTree seamlessly connects with{" "}
            <Link
              to="/category/celebrate-a-life"
              className="link-underline font-medium"
              data-testid="about-celebrate-link"
            >
              Celebrate a Life
            </Link>
            , allowing families to create meaningful memorials that honor
            parents, grandparents, siblings, relatives, and friends. Preserve
            their stories with:
          </Body>
          <Ledger items={MEMORIAL_ITEMS} columns={2} />
          <Body>
            These pages become lasting tributes that future generations can
            visit, learn from, and cherish.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 07 · Discover Your Heritage ---------------- */}
      <Chapter n="07" title="Discover" subtitle="your heritage.">
        <Prose>
          <Body>Family history is also cultural history.</Body>
          <Body>Explore the traditions that shaped your family:</Body>
          <Ledger items={HERITAGE_ITEMS} columns={3} />
          <Body>
            Understanding where your family came from helps preserve the values
            and traditions that continue to define it today.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 08 · A Living Family Archive ---------------- */}
      <Chapter n="08" title="A living" subtitle="family archive.">
        <Prose>
          <Body>
            Unlike traditional genealogy software, MyFamilyTree is designed as a
            living, collaborative archive. Invite relatives to contribute:
          </Body>
          <Ledger items={ARCHIVE_ITEMS} columns={2} />
          <Body>
            Together, your family can build a shared history that grows richer
            with each contribution.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 09 · Powered by SocialPulse ---------------- */}
      <Chapter n="09" title="Powered by" subtitle="SocialPulse.">
        <Prose>
          <Body>
            MyFamilyTree is fully integrated with the <SP /> ecosystem,
            connecting your family's story to the broader world. Discover
            related:
          </Body>
          <Ledger items={PULSE_ITEMS} columns={2} />
          <Body>
            Your family history becomes part of a larger story that connects
            people, places, and generations.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 10 · Built for Future Generations ---------------- */}
      <Chapter n="10" title="Built for" subtitle="future generations.">
        <Prose>
          <Body>Imagine your great-grandchildren discovering:</Body>
          <div className="border-l border-[color:var(--rust)] pl-6 md:pl-8 space-y-3 py-2">
            {FUTURE_ITEMS.map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.8vw, 26px)" }}
              >
                {line}
              </p>
            ))}
          </div>
          <Body>
            MyFamilyTree ensures these memories remain accessible for
            generations to come.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Closing manifesto ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-[color:var(--ink)]">
        <div className="overline text-[color:var(--ink-soft)] mb-6">
          § Begin your family journey
        </div>
        <div className="space-y-4 md:space-y-6 mb-14">
          {CLOSING_LINES.map((line, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease }}
              className="font-serif-display leading-[1.02]"
              style={{ fontSize: "clamp(26px, 4.5vw, 66px)" }}
            >
              {line}
            </motion.p>
          ))}
        </div>
        <Body>
          Start building your family tree today, preserve your heritage,
          celebrate the people who shaped your life, and create a lasting
          digital legacy that future generations will treasure.
        </Body>

        <div className="mt-10 flex flex-wrap gap-3">
          <a
            href={AUTH_SIGNUP_URL}
            className="btn-rust"
            data-testid="about-cta"
          >
            Begin your archive <span aria-hidden>→</span>
          </a>
          <Link to="/cemeteries" className="btn-ghost" data-testid="about-cta-explore">
            Explore the archive
          </Link>
        </div>
      </section>

      {/* ---------------- Signature ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
        <div className="border-t border-[color:var(--ink-line)] pt-10">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-7">
              <div
                className="font-serif-display leading-[0.95]"
                style={{ fontSize: "clamp(40px, 6vw, 96px)" }}
              >
                MyFamilyTree
                <span className="text-[color:var(--rust)]">.</span>care
              </div>
              <div
                className="font-italic-serif italic mt-3 text-[color:var(--ink-soft)]"
                style={{ fontSize: "clamp(18px, 2vw, 26px)" }}
              >
                Discover your roots · Celebrate your heritage · Preserve your legacy.
              </div>
            </div>
            <div className="col-span-12 md:col-span-5 md:text-right">
              <div className="overline text-[color:var(--ink-soft)] mb-2">
                § A part of
              </div>
              <a
                href="https://www.socialpulse.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-serif-display link-underline"
                style={{ fontSize: "clamp(22px, 2.4vw, 34px)" }}
                data-testid="about-socialpulse-link"
              >
                The SocialPulse Family & Heritage Network
              </a>
              <p className="mt-3 text-[13px] text-[color:var(--ink-soft)] max-w-sm md:ml-auto">
                Connecting families, stories, places and generations across the
                world.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
